import React from 'react';
import { useSelector } from 'react-redux';

import { formatCompactNumber, CompactNumberUnit } from '../utils/compactNumber';

import { getMarket } from '../selectors';
import {
  formatNumber,
  getCurrencyNumberFormatSeparators,
  getNumberFormatSeparators,
} from '../utils/l10n';

import type { DecimalValue } from '../config/types';
type Props = {
  market?: string;
  value: DecimalValue;
  decimals?: number;
  locale?: string;
  isCompact?: boolean;
  translateUnit?: (unit: CompactNumberUnit) => React.ReactNode;
};

const DEFAULT_DECIMALS = 0;

/**
 * Format number based on currency
 */
function getL10nCurrencyNumber(
  currency: string,
  value: DecimalValue,
  decimals = DEFAULT_DECIMALS,
  shouldDisplayOriginalValue = false,
) {
  const separators = getCurrencyNumberFormatSeparators(currency);
  return formatNumber(value, decimals, shouldDisplayOriginalValue, separators);
}

/**
 * Format number based on market
 */
function getL10nNumber(
  market: string,
  value: DecimalValue,
  decimals = DEFAULT_DECIMALS,
  shouldDisplayOriginalValue = false,
) {
  const separators = getNumberFormatSeparators(market);
  return formatNumber(value, decimals, shouldDisplayOriginalValue, separators);
}

function L10nNumber({
  market,
  value,
  decimals = DEFAULT_DECIMALS,
  isCompact = false,
  translateUnit,
}: Props) {
  const marketFromStore = useSelector(getMarket)?.marketCode || '';
  if (!value && value !== 0) return null;
  if (isCompact) {
    const numValue = typeof value === 'number' ? value : parseFloat(value.toString());
    const { value: formattedValue, unit } = formatCompactNumber(numValue);
    // Currently only used for whole numbers or in contexts where decimal localization is not required.
    return (
      <span>
        {formattedValue}
        {unit && translateUnit && translateUnit(unit)}
      </span>
    );
  }
  return <span>{getL10nNumber(market || marketFromStore, value, decimals)}</span>;
}

export default L10nNumber;

export { getL10nNumber, getL10nCurrencyNumber };
